import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { DataGrid } from "@mui/x-data-grid";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { roleAccess } from "../EboPaymentWorking";

const getResponseRows = (data) => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data?.result)) return data.result;
	if (Array.isArray(data?.results)) return data.results;
	if (Array.isArray(data?.rows)) return data.rows;
	return [];
};

const normalizeMappedFranchise = (row, index) => ({
	id: row?.id ?? row?._id ?? row?.franchise_id ?? `mapped-franchise-${index + 1}`,
	name: row?.name ?? row?.franchise_name ?? row?.franchise ?? row?.label ?? "",
	stores: getResponseRows(row?.stores || row?.mapped_stores || row?.franchise_stores || row?.store_list || row?.storeList),
});

const getStoreName = (row) => row?.store_name || row?.store_display_name || row?.name || row?.storeName || "";
const getStoreCode = (row) => row?.store_code || row?.code || row?.storeCode || row?.short_code || row?.SHORT_CODE || "";
const getStoreStatus = (row) => String(row?.store_status || "enabled").toLowerCase();
const getCommissionPercent = (row) => row?.commission_percent ?? row?.commissionPercentage ?? row?.comm_percent ?? "";
const getEffectiveDate = (row) => row?.effective_date ?? row?.effectiveDate ?? row?.effected_date ?? "";
const formatHeaderName = (field) =>
	String(field || "")
		.replace(/_/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.trim()
		.toUpperCase();
const formatCellValue = (value) => {
	if (value === null || value === undefined) return "";
	if (typeof value === "object") return JSON.stringify(value);
	return value;
};

const downloadCsv = (rows, fileName, headers) => {
	const csvRows = [
		headers.join(","),
		...rows.map((row) =>
			headers
				.map((header) => {
					const value = row?.[header] ?? "";
					const escaped = String(value).replace(/"/g, '""');
					return `"${escaped}"`;
				})
				.join(",")
		),
	];

	const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", fileName);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

function COFOCommissionMaster() {
	const [franchiseList, setFranchiseList] = useState([]);
	const [selectedFranchise, setSelectedFranchise] = useState("");
	const [commissionRows, setCommissionRows] = useState([]);
	const [selectedFile, setSelectedFile] = useState(null);
	const [uploadedFileName, setUploadedFileName] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	
	const roleAccessData = roleAccess(user);
	const isReadOnlyUser = ["VIEWER", "EDITOR"].includes(roleAccessData?.roles);

	const columns = useMemo(() => {
		const firstRow = commissionRows[0];
		if (!firstRow) return [];

		return Object.keys(firstRow)
			.filter((field) => field !== "id")
			.map((field) => ({
				field,
				headerName: formatHeaderName(field),
				flex: 1,
				minWidth: 160,
				valueFormatter: (value) => formatCellValue(value),
			}));
	}, [commissionRows]);

	const handleGetFranchiseList = async () => {
		try {
			setLoadingOpen(true);
			const res = await pythonVmInstance({
				url: "/ebo-payment-working/cofo/mapped-stores",
				headers: { "Content-Type": "application/json" },
				method: "GET",
			});
			const rows = getResponseRows(res?.data).map(normalizeMappedFranchise).filter((row) => row?.name);
			setFranchiseList(rows);
			const initialFranchiseId = rows[0]?.id || "";
			setSelectedFranchise((currentSelected) => currentSelected || initialFranchiseId);
			if (initialFranchiseId) {
				await handleLoadCommissionRows(initialFranchiseId, false);
			}
		} catch (fetchError) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: fetchError?.response?.data?.message || "Failed to fetch franchise list",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	useEffect(() => {
		handleGetFranchiseList();
	}, []);

	const buildCommissionRows = (rows, franchiseName, franchiseId) =>
		getResponseRows(rows).map((row, index) => ({
			id: row?.id ?? row?._id ?? `${franchiseId}-${index + 1}`,
			franchise_name: row?.franchise || franchiseName || "",
			store_name: getStoreName(row),
			store_code: getStoreCode(row),
			commission_percent: getCommissionPercent(row),
			effective_date: getEffectiveDate(row),
		}));

	const handleLoadCommissionRows = async (franchiseId, msg = false) => {
		if (!franchiseId) {
			setCommissionRows([]);
			return;
		}

		try {
			setLoadingOpen(true);
			setError("");
			setSuccess("");
			const franchiseRecord = franchiseList.find((franchise) => String(franchise.id) === String(franchiseId));
			const res = await pythonVmInstance({
				url: "/ebo-payment-working/cofo/stores-comm",
				headers: { "Content-Type": "application/json" },
				method: "GET",
			});

			const rows = buildCommissionRows(res?.data, franchiseRecord?.name || "", franchiseId);
			setCommissionRows(rows);
			setSuccess(rows.length && msg ? "Commission rows loaded successfully." : msg && "No commission rows found for selected franchise.");
		} catch (fetchError) {
			setCommissionRows([]);
			setError(fetchError?.response?.data?.message || "Failed to fetch commission data.");
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleFileSelect = (event) => {
		const file = event.target.files?.[0] || null;
		event.target.value = "";
		if (!file) return;
		if (!file.name.toLowerCase().endsWith(".csv")) {
			setSelectedFile(null);
			setUploadedFileName("");
			setError("Please select a CSV file.");
			return;
		}
		setSelectedFile(file);
		setUploadedFileName(file.name);
		setError("");
		setSuccess("");
	};

	const handleFileUpload = async () => {
		if (!selectedFranchise) {
			setError("Please select a franchise before uploading.");
			return;
		}
		if (!selectedFile) {
			setError("Please select a CSV file to upload.");
			return;
		}

		try {
			setLoadingOpen(true);
			setError("");
			const franchiseRecord = franchiseList.find((franchise) => String(franchise.id) === String(selectedFranchise));
			const form = new FormData();
			form.append("files", selectedFile);
			form.append("email", user?.email || "");
			form.append("store_type", "cofo");
			form.append("franchise_name", franchiseRecord?.name || "");

			await pythonVmInstance.post("/ebo-payment-working/upload-comm", form, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			await handleLoadCommissionRows(selectedFranchise);
			setSelectedFile(null);
			setUploadedFileName("");
			setSuccess("Updated commission uploaded successfully.");
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Updated commission uploaded successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (uploadError) {
			setError(uploadError?.response?.data?.message || "Failed to upload updated commission.");
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleDownload = () => {
		if (!selectedFranchise) return;
		const franchiseRecord = franchiseList.find((franchise) => String(franchise.id) === String(selectedFranchise));
		const rows = getResponseRows(franchiseRecord?.stores)
			.filter((store) => getStoreStatus(store) === "enabled")
			.map((store) => ({
				franchise: franchiseRecord?.name || "",
				store_name: getStoreName(store),
				store_code: getStoreCode(store),
				commission_percent: getCommissionPercent(store) ?? 0,
				effective_date: getEffectiveDate(store) ?? "",
			}));
		if (!franchiseRecord || !rows.length) {
			setError("No enabled stores found for selected franchise.");
			return;
		}
		const safeName = String(franchiseRecord?.name || selectedFranchise).toLowerCase().replace(/[^a-z0-9]+/g, "_");
		downloadCsv(rows, `${safeName}_cofo_commission_master.csv`, [
			"franchise",
			"store_name",
			"store_code",
			"commission_percent",
			"effective_date",
		]);
	};

	const franchiseCommRecord = commissionRows.filter(
		(franchise) =>
			String(franchise.franchise_name) === String(franchiseList.find((item) => String(item.id) === String(selectedFranchise))?.name || "")
    );

	return (
		<Box width="100%" p={1.5} sx={{ background: "linear-gradient(180deg, #fafafa 0%, #ffffff 100%)" }}>
			<Stack spacing={1.5}>
				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
					<Stack spacing={1.5}>
						<Box>
							<Typography variant="subtitle1" fontWeight={700}>
								COFO Commission Master
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
								Select a franchise, download its commission sheet, then upload the updated commission file.
							</Typography>
						</Box>

						<TextField
							select
							size="small"
							label="Select Franchise"
							value={selectedFranchise}
							onChange={(event) => {
								setSelectedFranchise(event.target.value);
								setError("");
								setSuccess("");
							}}
							sx={{
								maxWidth: 320,
								"& .MuiInputBase-root": { height: 38, borderRadius: 1.5 },
								"& .MuiInputBase-input": { py: 0.85, fontSize: 13 },
							}}>
							{franchiseList.length === 0 ? (
								<MenuItem value="" disabled>
									No franchise available
								</MenuItem>
							) : (
								franchiseList.map((franchise) => (
									<MenuItem key={franchise.id} value={franchise.id}>
										{franchise.name}
									</MenuItem>
								))
							)}
						</TextField>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
								gap: 1.5,
							}}>
							<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "rgba(59,130,246,0.03)" }}>
								<Stack spacing={1.25}>
									<Box>
										<Typography variant="subtitle2" fontWeight={700}>
											Download Template
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Download the current commission sheet for the selected franchise.
										</Typography>
									</Box>
									<Button
										variant="outlined"
										startIcon={<FileDownloadIcon />}
										onClick={handleDownload}
										disabled={!selectedFranchise || isReadOnlyUser}
										size="small"
										sx={{ height: 38, px: 2, alignSelf: "flex-start" }}>
										Download
									</Button>
								</Stack>
							</Paper>

							<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "rgba(16,185,129,0.03)" }}>
								<Stack spacing={1.25}>
									<Box>
										<Typography variant="subtitle2" fontWeight={700}>
											Upload Updated File
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Choose the updated CSV and upload it for the selected franchise.
										</Typography>
									</Box>
									<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
										<Button
											component="label"
											variant="outlined"
											startIcon={<UploadFileIcon />}
											disabled={isReadOnlyUser}
											size="small"
											sx={{ height: 38, px: 2, alignSelf: "flex-start" }}>
											Select CSV
											<input hidden accept=".csv,text/csv" type="file" onChange={handleFileSelect} disabled={isReadOnlyUser} />
										</Button>
										<Button
											variant="contained"
											onClick={handleFileUpload}
											disabled={!selectedFranchise || !selectedFile ||isReadOnlyUser}
											size="small"
											sx={{ height: 38, px: 2, alignSelf: "flex-start" }}>
											Upload Commission
										</Button>
									</Stack>
								</Stack>
							</Paper>
						</Box>

						{uploadedFileName ? (
							<Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
								Selected File: {uploadedFileName}
							</Typography>
						) : null}
						{error ? <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert> : null}
						{success ? <Alert severity="success" sx={{ py: 0.5 }}>{success}</Alert> : null}
					</Stack>
				</Paper>

				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
					<Stack spacing={1.25}>
						<Typography variant="subtitle1" fontWeight={700}>
							Franchise Store Columns
						</Typography>
						<Box sx={{ height: { xs: 420, md: 500 }, width: "100%" }}>
							<DataGrid
								rows={franchiseCommRecord}
								columns={columns}
								disableColumnMenu
								disableRowSelectionOnClick
								density="compact"
								pageSizeOptions={[25, 50, 100]}
								initialState={{
									pagination: {
										paginationModel: { pageSize: 25, page: 0 },
									},
								}}
								sx={{
									border: "none",
									fontSize: 13,
									"& .MuiDataGrid-row": { minHeight: 36, maxHeight: 36 },
									"& .MuiDataGrid-cell": { py: 0.5 },
									"& .MuiDataGrid-columnHeaders": {
										backgroundColor: "#f5f5f5",
										minHeight: 40,
										maxHeight: 40,
									},
								}}
							/>
						</Box>
					</Stack>
				</Paper>
			</Stack>
		</Box>
	);
}

export default COFOCommissionMaster;
